import React, { Suspense, lazy } from "react";
import type { ShopSettingsProps } from "./ShopSettings";

// react-on-rails render function. The real page (Polaris + Apollo + the form) is loaded
// lazily so @shopify/polaris isn't bundled into the synchronous client-bundle that every
// page loads. The `import type` above is erased at compile time, so it doesn't pull Polaris
// in here.
const App = lazy(() => import("./App"));

// Dependency-free spinner for the Suspense fallback. We deliberately do NOT import Polaris
// here — keeping it out of the synchronous bundle is the whole point of lazy-loading ./App
// below — so the fallback can't use Polaris's <Spinner>. An inline SVG with a SMIL rotate is
// fully self-contained (no CSS keyframes, no extra imports) and matches the Polaris palette.
function LoadingState() {
  return (
    <div
      role="status"
      aria-live="polite"
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 12,
        minHeight: 240,
        padding: 16,
        color: "#6d7175",
      }}
    >
      <svg width="36" height="36" viewBox="0 0 50 50" aria-hidden="true">
        <circle cx="25" cy="25" r="20" fill="none" stroke="#e1e3e5" strokeWidth="5" />
        <path fill="none" stroke="#5c6ac4" strokeWidth="5" strokeLinecap="round" d="M25 5a20 20 0 0 1 0 40">
          <animateTransform
            attributeName="transform"
            type="rotate"
            from="0 25 25"
            to="360 25 25"
            dur="0.7s"
            repeatCount="indefinite"
          />
        </path>
      </svg>
      <span>Loading settings…</span>
    </div>
  );
}

export default function (props: ShopSettingsProps, _railsContext?: unknown) {
  return function ShopSettingsApp() {
    return (
      <Suspense fallback={<LoadingState />}>
        <App {...props} />
      </Suspense>
    );
  };
}
